import { PropsWithChildren } from "react"
import { Button, ButtonProps } from "./buttons/Button"
import { Msg } from "./Msg"
import { classes } from "./react"

export type DialogFooterProps = {
    className?: string
}

export const DialogFooter: React.FunctionComponent<React.PropsWithChildren<DialogFooterProps>> = ({
    children,
    className,
}) => <div className={classes("modal-footer", className)}>{children}</div>

export type DialogFooterButtonsProps = {
    /**
     * Any additional CSS `className` for the component
     */
    className?: string
    /**
     * If <c>true</c>, the "Save" button will never be displayed (since the dialog is "read-only").
     *
     * @default false
     */
    isReadOnly?: boolean
    /**
     * If <c>true</c>, the "Cancel" & "Save" buttons are visible; otherwise, the "Close" button is visible.
     *
     * @default false
     */
    hasChanges?: boolean
    /**
     * If <c>true</c>, the "Save" button is clickable; otherwise, the "Save" button is disabled.
     *
     * @default false
     */
    canCommit?: boolean
    /**
     * Event handler for when the "Close" button is clicked.
     */
    onClose: React.MouseEventHandler<HTMLButtonElement>
    /**
     * Event handler for when the "Cancel" button is clicked.
     *
     * If unspecified, uses the `onClose` event handler instead.
     */
    onCancel?: React.MouseEventHandler<HTMLButtonElement>
    /**
     * Event handler for when the "Save" button is clicked.
     *
     * If unspecified, the "Cancel"/"Save" buttons will not be displayed.
     */
    onCommit?: React.MouseEventHandler<HTMLButtonElement>
    /**
     * (Optional) Specify custom text for the "Save" button.
     */
    saveButtonText?: React.ReactNode
    /**
     * (Optional) Specify custom text for the "Save" button.
     */
    closeButtonText?: React.ReactNode
    /**
     * (Optional) Specify custom text for the "Save" button.
     */
    cancelButtonText?: React.ReactNode
}

export const DialogFooterButtons = ({
    className,
    children,
    isReadOnly = false,
    canCommit = false,
    hasChanges = false,
    onClose,
    onCancel = onClose,
    onCommit,
    saveButtonText,
    closeButtonText,
    cancelButtonText,
}: PropsWithChildren<DialogFooterButtonsProps>) => (
    <div className={classes("modal-footer-buttons", className)}>
        {children}
        {onCommit && hasChanges && !isReadOnly ? (
            <>
                <CancelButton onClick={onCancel} children={cancelButtonText} />
                <SaveButton onClick={onCommit} disabled={!canCommit} children={saveButtonText} />
            </>
        ) : (
            <CloseButton onClick={onClose} children={closeButtonText} />
        )}
    </div>
)

/**
 * A button to be used for the "Save/Commit" action, (appears as the "success" variant)
 */
export const SaveButton = ({ children = <Msg>Save</Msg>, ...props }: Omit<ButtonProps, "variant">) => (
    <Button {...props} variant="success">
        {children}
    </Button>
)

/**
 * A button to be used for the "Cancel" action, (appears as the "warning" variant)
 */
export const CancelButton = ({ children = <Msg>Cancel</Msg>, ...props }: Omit<ButtonProps, "variant">) => (
    <Button {...props} variant="warning">
        {children}
    </Button>
)

/**
 * A button to be used for the "Close" action, (appears as the "primary" variant)
 */
export const CloseButton = ({ children = <Msg>Close</Msg>, ...props }: Omit<ButtonProps, "variant">) => (
    <Button {...props} variant="primary">
        {children}
    </Button>
)

/**
 * A default button, to be used for whatever, (appears as the "default" variant)
 */
export const DefaultButton = (props: Omit<ButtonProps, "variant">) => <Button {...props} variant="default" />

/**
 * A button for any dangerous action, (appears as the "danger" variant)
 */
export const DangerButton = (props: Omit<ButtonProps, "variant">) => <Button {...props} variant="danger" />
